import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useOrgParams } from '~/common/use-org-params'
import { useSeasonList } from '~/farm/use-season-list'
import { useFeatures } from '~/common/use-features'
import { useOrgContextDispatch, setLastSeasonId } from '~/common/context/organization/org-context'
import { useSeasonId } from '~/common/use-season-id'

export const SeasonSelector = () => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [wrapperEl, setWrapperEl] = useState(null)
  const { farmId } = useOrgParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { seasons, loading } = useSeasonList({ farmId })
  const { features } = useFeatures()
  const dispatch = useOrgContextDispatch()

  const { seasonId } = useSeasonId({ farmId, seasons, loading })

  // Sync URL — fires when no season in URL, or when the URL season is stale/invalid for this farm
  const urlSeasonId = searchParams.get('seasonId')
  useEffect(() => {
    if (!farmId || loading || seasons.length === 0 || !seasonId) return
    const urlSeasonValid = urlSeasonId && seasons.some(s => s.id === urlSeasonId)
    if (!urlSeasonValid) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.set('seasonId', seasonId)
        return next
      }, { replace: true })
    }
  }, [seasonId, urlSeasonId, farmId, loading, seasons, setSearchParams])

  const handleSelect = (newSeasonId) => {
    setAnchorEl(null)

    if (farmId) {
      localStorage.setItem(`lastSeasonId_${farmId}`, newSeasonId)
    }
    dispatch(setLastSeasonId({ seasonId: newSeasonId }))

    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newSeasonId) next.set('seasonId', newSeasonId)
      else next.delete('seasonId')
      return next
    })
  }

  if (!features.season_selector) return null
  if (!farmId || loading || seasons.length === 0) return null

  const selectedSeason = seasons.find(s => s.id === seasonId)
  const open = Boolean(anchorEl)

  return (
    <Box
      ref={setWrapperEl}
      sx={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        mr: 0.5,
        position: 'relative',
        transition: 'background-color 0.2s',
        px: 1,

        '&:hover::before': {
          content: '""',
          position: 'absolute',
          top: 8,
          bottom: 8,
          left: 0,
          right: 0,
          borderRadius: 1,
          border: '4px solid rgba(255,255,255,0.1)',
          pointerEvents: 'none',
        },
      }}
    >
      <Stack
        onClick={() => setAnchorEl(wrapperEl)}
        direction='row'
        spacing={1}
        sx={{ cursor: 'pointer' }}
      >
        {selectedSeason && (
          <Typography
            variant="body2"
            sx={{ color: 'inherit', mr: 0.25, userSelect: 'none' }}
          >
            {selectedSeason.year}
          </Typography>
        )}
        <Stack>
          <Icons.CalendarMonth fontSize="small" />
        </Stack>
      </Stack>
      <Menu
        id="season-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={0}
      >
        {seasons.map(s => (
          <MenuItem
            key={s.id}
            selected={s.id === seasonId}
            onClick={() => handleSelect(s.id)}
          >
            {s.year}{s.state === 'active' ? ' (Active)' : ''}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
